import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/hooks/useAuth";
import ErrorBoundary from "@/components/ErrorBoundary";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import RepRoute from "@/components/RepRoute";
import AmRoute from "@/components/AmRoute";
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import InstallPrompt from "@/components/InstallPrompt";
import ViewAsBanner from "@/components/ViewAsBanner";


// Lazy-loaded routes — keeps initial bundle small for landing/login.
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const QuoteWizard = lazy(() => import("./pages/QuoteWizard"));
const RepDashboard = lazy(() => import("./pages/RepDashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const AdminPricing = lazy(() => import("./pages/AdminPricing"));
// AdminAnalytics removed — consolidated into the unified /reports page.
const FactoryDashboard = lazy(() => import("./pages/FactoryDashboard"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminQuickshipImport = lazy(() => import("./pages/AdminQuickshipImport"));
const AdminEmailDashboard = lazy(() => import("./pages/AdminEmailDashboard"));
const AdminLoyalty = lazy(() => import("./pages/AdminLoyalty"));
const AdminDataExport = lazy(() => import("./pages/AdminDataExport"));
const AdminErrorLog = lazy(() => import("./pages/AdminErrorLog"));
const AdminSupport = lazy(() => import("./pages/AdminSupport"));
const AdminDealerAccounts = lazy(() => import("./pages/AdminDealerAccounts"));
const PurchaseOrders = lazy(() => import("./pages/PurchaseOrders"));
const CreatePO = lazy(() => import("./pages/CreatePO"));
const WarrantySupport = lazy(() => import("./pages/WarrantySupport"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Reports = lazy(() => import("./pages/Reports"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const AdminHub = lazy(() => import("./pages/AdminHub"));
const AmDashboard = lazy(() => import("./pages/AmDashboard"));
const QuoteSuccess = lazy(() => import("./pages/QuoteSuccess"));
const PoSuccess = lazy(() => import("./pages/PoSuccess"));
const AdminInvoices = lazy(() => import("./pages/AdminInvoices"));
const Guide = lazy(() => import("./pages/Guide"));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
    mutations: {
      retry: 0,
    },
  },
});

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
  </div>
);

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <ErrorBoundary>
            <Toaster />
            <Sonner />
            <InstallPrompt />
            <BrowserRouter>
              <ViewAsBanner />
              <Suspense fallback={<RouteFallback />}>

                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/quote" element={<ProtectedRoute><QuoteWizard /></ProtectedRoute>} />
                  <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                  <Route path="/admin" element={<AdminRoute><AdminHub /></AdminRoute>} />
                  <Route path="/am" element={<AmRoute><AmDashboard /></AmRoute>} />
                  <Route path="/admin/pricing" element={<AdminRoute><AdminPricing /></AdminRoute>} />
                  <Route path="/admin/analytics" element={<Navigate to="/reports" replace />} />
                  <Route path="/admin/factory" element={<AdminRoute><FactoryDashboard /></AdminRoute>} />
                  <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
                  <Route path="/admin/quickship-import" element={<AdminRoute><AdminQuickshipImport /></AdminRoute>} />
                  <Route path="/admin/emails" element={<AdminRoute><AdminEmailDashboard /></AdminRoute>} />
                  <Route path="/admin/loyalty" element={<AdminRoute><AdminLoyalty /></AdminRoute>} />
                  <Route path="/admin/export" element={<AdminRoute><AdminDataExport /></AdminRoute>} />
                  <Route path="/admin/errors" element={<AdminRoute><AdminErrorLog /></AdminRoute>} />
                  <Route path="/admin/support" element={<AdminRoute><AdminSupport /></AdminRoute>} />
                  <Route path="/admin/invoices" element={<AmRoute><AdminInvoices /></AmRoute>} />
                  <Route path="/admin/dealer-accounts" element={<AdminRoute><AdminDealerAccounts /></AdminRoute>} />
                  <Route path="/po" element={<ProtectedRoute><PurchaseOrders /></ProtectedRoute>} />
                  <Route path="/rep" element={<RepRoute><RepDashboard /></RepRoute>} />
                  <Route path="/warranty" element={<ProtectedRoute><WarrantySupport /></ProtectedRoute>} />
                  <Route path="/po/new" element={<ProtectedRoute><CreatePO /></ProtectedRoute>} />
                  <Route path="/po/success" element={<ProtectedRoute><PoSuccess /></ProtectedRoute>} />
                  <Route path="/quote/success" element={<ProtectedRoute><QuoteSuccess /></ProtectedRoute>} />
                  <Route path="/guide" element={<AdminRoute><Guide /></AdminRoute>} />
                  <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
                  
                  <Route path="/accept-invite" element={<AcceptInvite />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </ErrorBoundary>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
